import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Badge
} from "reactstrap";
import { 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaInfoCircle,
  FaCalendarAlt,
  FaUserTie,
  FaBuilding,
  FaHistory,
  FaExclamationCircle
} from "react-icons/fa";

const API_BASE_URL = "https://api.editezy.com/api/admin";

const GetAboutUsPage = () => {
  const [aboutUsData, setAboutUsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState("");

  // Validation states
  const [errors, setErrors] = useState({
    title: "",
    content: "",
    date: ""
  });
  const [touched, setTouched] = useState({
    title: false,
    content: false,
    date: false
  });

  useEffect(() => {
    const fetchAboutUsData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/getaboutus`);
        
        console.log("API Response:", response.data);

        // Check if response has _id (actual data)
        if (response.data && response.data._id) {
          setAboutUsData(response.data);
          setTitle(response.data.title || "");
          setContent(response.data.content || "");
          setDate(response.data.date ? new Date(response.data.date).toISOString().slice(0, 10) : "");
          setApiMessage("");
        } 
        // Check if response has message (like "About Us not found")
        else if (response.data && response.data.message) {
          setApiMessage(response.data.message);
          setMessageType("info");
          setAboutUsData(null);
        } 
        // Fallback
        else {
          setApiMessage("No data available");
          setMessageType("info");
          setAboutUsData(null);
        }
      } catch (err) {
        console.error("Error fetching About Us data:", err);
        
        if (err.response && err.response.data && err.response.data.message) {
          setApiMessage(err.response.data.message);
          setMessageType("error");
          setAboutUsData(null);
        } else if (err.response) {
          if (err.response.status === 404) {
            setApiMessage("About Us endpoint not found");
          } else {
            setApiMessage(`Server error: ${err.response.status}`);
          }
          setMessageType("error");
        } else if (err.request) {
          setApiMessage("Network error - please check your connection");
          setMessageType("error");
        } else {
          setApiMessage("An unexpected error occurred");
          setMessageType("error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAboutUsData();
  }, []);

  // Validation functions
  const validateTitle = (value) => {
    if (!value || value.trim() === "") {
      return "Title is required";
    }
    if (value.trim().length < 3) {
      return "Title must be at least 3 characters";
    }
    if (value.trim().length > 100) {
      return "Title must not exceed 100 characters";
    }
    return "";
  };

  const validateContent = (value) => {
    if (!value || value.trim() === "") {
      return "Content is required";
    }
    if (value.trim().length < 10) {
      return "Content must be at least 10 characters";
    }
    if (value.trim().length > 5000) {
      return "Content must not exceed 5000 characters";
    }
    return "";
  };

  const validateDate = (value) => {
    if (!value) {
      return "Date is required";
    }
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return "Date cannot be in the past";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "title":
        error = validateTitle(value);
        break;
      case "content":
        error = validateContent(value);
        break;
      case "date":
        error = validateDate(value);
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
    return error;
  };

  const handleFieldChange = (field, value) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    switch (field) {
      case "title":
        setTitle(value);
        break;
      case "content":
        setContent(value);
        break;
      case "date":
        setDate(value);
        break;
      default:
        break;
    }
    
    validateField(field, value);
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let value;
    switch (field) {
      case "title":
        value = title;
        break;
      case "content":
        value = content;
        break;
      case "date":
        value = date;
        break;
      default:
        return;
    }
    validateField(field, value);
  };

  const validateForm = () => {
    const titleError = validateTitle(title);
    const contentError = validateContent(content);
    const dateError = validateDate(date);
    
    setErrors({
      title: titleError,
      content: contentError,
      date: dateError
    });
    
    setTouched({
      title: true,
      content: true,
      date: true
    });
    
    return !titleError && !contentError && !dateError;
  };

  // Handle update API call
  const handleUpdate = async () => {
    // Validate form before submission
    if (!validateForm()) {
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.focus();
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);
    setApiMessage("");
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/updateaboutus/${aboutUsData._id}`,
        { 
          title: title.trim(), 
          content: content.trim(), 
          date 
        }
      );
      
      setApiMessage("About Us updated successfully!");
      setMessageType("success");
      
      if (response.data && response.data._id) {
        setAboutUsData(response.data);
        setTitle(response.data.title || "");
        setContent(response.data.content || "");
        setDate(response.data.date ? new Date(response.data.date).toISOString().slice(0, 10) : "");
      } else if (response.data && response.data.aboutUs) {
        setAboutUsData(response.data.aboutUs);
        setTitle(response.data.aboutUs.title || "");
        setContent(response.data.aboutUs.content || "");
        setDate(response.data.aboutUs.date ? new Date(response.data.aboutUs.date).toISOString().slice(0, 10) : "");
      }
      
      setEditMode(false);
      setErrors({ title: "", content: "", date: "" });
      setTouched({ title: false, content: false, date: false });
      
      setTimeout(() => {
        setApiMessage("");
      }, 3000);
      
    } catch (err) {
      console.error("Error updating About Us:", err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setApiMessage(err.response.data.message);
        setMessageType("error");
      } else {
        setApiMessage("Failed to update About Us. Please try again.");
        setMessageType("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete API call
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete the About Us data? This action cannot be undone.")) {
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.delete(
        `${API_BASE_URL}/deleteaboutus/${aboutUsData._id}`
      );
      
      setApiMessage("About Us deleted successfully!");
      setMessageType("success");
      
      setAboutUsData(null);
      setTitle("");
      setContent("");
      setDate("");
      setEditMode(false);
      setErrors({ title: "", content: "", date: "" });
      setTouched({ title: false, content: false, date: false });
      
      setTimeout(() => {
        setApiMessage("");
      }, 3000);
      
    } catch (err) {
      console.error("Error deleting About Us:", err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setApiMessage(err.response.data.message);
        setMessageType("error");
      } else {
        setApiMessage("Failed to delete About Us. Please try again.");
        setMessageType("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setApiMessage("");
    setErrors({ title: "", content: "", date: "" });
    setTouched({ title: false, content: false, date: false });
    
    if (aboutUsData && aboutUsData._id) {
      setTitle(aboutUsData.title || "");
      setContent(aboutUsData.content || "");
      setDate(aboutUsData.date ? new Date(aboutUsData.date).toISOString().slice(0, 10) : "");
    } else {
      setTitle("");
      setContent("");
      setDate("");
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return title.trim() && content.trim() && date && 
           !errors.title && !errors.content && !errors.date;
  };

  // Loading State
  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow-lg border-0">
              <CardBody className="text-center py-5">
                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }}>
                  Loading...
                </Spinner>
                <p className="mt-3 text-muted">Loading About Us information...</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow-lg border-0">
            <CardHeader 
              className="py-3 d-flex align-items-center justify-content-between"
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                borderBottom: 'none'
              }}
            >
              <div className="d-flex align-items-center">
                <FaBuilding className="me-2" size={24} />
                <h4 className="mb-0">About Us Management</h4>
              </div>
              {!editMode && aboutUsData && aboutUsData._id && (
                <Badge color="light" pill className="px-3 py-2">
                  Active
                </Badge>
              )}
              {editMode && (
                <Badge color="warning" pill className="px-3 py-2">
                  Editing
                </Badge>
              )}
            </CardHeader>
            
            <CardBody className="p-4">
              {/* API Message Display */}
              {apiMessage && (
                <Alert 
                  color={
                    messageType === 'success' ? 'success' : 
                    messageType === 'error' ? 'danger' : 
                    'info'
                  }
                  className="d-flex align-items-center border-0 shadow-sm mb-4"
                  style={{
                    borderLeft: messageType === 'success' ? '4px solid #28a745' : 
                               messageType === 'error' ? '4px solid #dc3545' : 
                               '4px solid #17a2b8'
                  }}
                >
                  <FaInfoCircle className={`me-3 ${
                    messageType === 'success' ? 'text-success' : 
                    messageType === 'error' ? 'text-danger' : 
                    'text-primary'
                  }`} size={20} />
                  <div>
                    <strong className="d-block mb-1">
                      {messageType === 'success' ? '✓ Success' : 
                       messageType === 'error' ? '✗ Error' : 
                       'ℹ Information'}
                    </strong>
                    <p className="mb-0">{apiMessage}</p>
                  </div>
                </Alert>
              )}

              {/* No About Us Found State */}
              {!aboutUsData && !editMode && (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <div className="position-relative d-inline-block">
                      <FaBuilding size={80} className="text-muted" style={{ opacity: 0.3 }} />
                      <FaInfoCircle 
                        size={30} 
                        className="text-primary position-absolute" 
                        style={{ bottom: 0, right: -10 }}
                      />
                    </div>
                  </div>
                  <h4 className="text-muted mb-3">No About Us Information Found</h4>
                  <p className="text-muted mb-0" style={{ maxWidth: '400px', margin: '0 auto' }}>
                    {apiMessage || "There is no About Us content available."}
                  </p>
                </div>
              )}

              {/* Edit Mode Form */}
              {editMode && aboutUsData && (
                <Form>
                  <FormGroup>
                    <Label for="title" className="fw-bold">
                      <FaUserTie className="me-2 text-primary" />
                      Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="title"
                      value={title}
                      onChange={(e) => handleFieldChange("title", e.target.value)}
                      onBlur={() => handleBlur("title")}
                      placeholder="Enter title (e.g., About Our Company)"
                      className={touched.title && errors.title ? 'is-invalid' : ''}
                      required
                    />
                    {touched.title && errors.title && (
                      <div className="text-danger small mt-1 d-flex align-items-center">
                        <FaExclamationCircle className="me-1" size={12} />
                        {errors.title}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="content" className="fw-bold">
                      <FaHistory className="me-2 text-primary" />
                      Content <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      id="content"
                      value={content}
                      onChange={(e) => handleFieldChange("content", e.target.value)}
                      onBlur={() => handleBlur("content")}
                      placeholder="Tell your story, mission, vision, and values..."
                      className={touched.content && errors.content ? 'is-invalid' : ''}
                      style={{ minHeight: '250px' }}
                      required
                    />
                    {touched.content && errors.content && (
                      <div className="text-danger small mt-1 d-flex align-items-center">
                        <FaExclamationCircle className="me-1" size={12} />
                        {errors.content}
                      </div>
                    )}
                    <div className="text-muted small mt-1">
                      {content.length}/5000 characters
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label for="date" className="fw-bold">
                      <FaCalendarAlt className="me-2 text-primary" />
                      Publication Date <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="date"
                      id="date"
                      value={date}
                      onChange={(e) => handleFieldChange("date", e.target.value)}
                      onBlur={() => handleBlur("date")}
                      className={touched.date && errors.date ? 'is-invalid' : ''}
                      required
                    />
                    {touched.date && errors.date && (
                      <div className="text-danger small mt-1 d-flex align-items-center">
                        <FaExclamationCircle className="me-1" size={12} />
                        {errors.date}
                      </div>
                    )}
                  </FormGroup>

                  <div className="d-flex gap-2 mt-4">
                    <Button
                      color="primary"
                      onClick={handleUpdate}
                      disabled={submitting || !isFormValid()}
                      className="px-4"
                      size="lg"
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Update About Us
                        </>
                      )}
                    </Button>
                    
                    <Button
                      color="secondary"
                      onClick={handleCancel}
                      disabled={submitting}
                      size="lg"
                    >
                      <FaTimes className="me-2" />
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}

              {/* View Mode - Shows when about us exists */}
              {aboutUsData && aboutUsData._id && !editMode && (
                <>
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                      <h2 className="text-primary mb-2">{aboutUsData.title}</h2>
                      <div className="d-flex align-items-center text-muted">
                        <FaCalendarAlt className="me-2" size={14} />
                        <small>
                          Published on: {new Date(aboutUsData.date).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </small>
                      </div>
                      <div className="d-flex align-items-center text-muted mt-1">
                        <small>
                          Last Updated: {new Date(aboutUsData.updatedAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </small>
                      </div>
                    </div>
                    <Badge color="success" pill className="px-3 py-2">
                      <FaInfoCircle className="me-1" size={12} />
                      Active
                    </Badge>
                  </div>

                  <div className="border rounded p-4 bg-light mb-4">
                    <div className="text-gray-700 leading-relaxed" style={{ whiteSpace: 'pre-line' }}>
                      {aboutUsData.content}
                    </div>
                  </div>

                  <div className="d-flex gap-3">
                    <Button
                      color="primary"
                      onClick={() => setEditMode(true)}
                      className="px-4"
                      size="lg"
                      disabled={submitting}
                    >
                      <FaEdit className="me-2" />
                      Edit Content
                    </Button>
                    
                    <Button
                      color="danger"
                      onClick={handleDelete}
                      disabled={submitting}
                      className="px-4"
                      size="lg"
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <FaTrash className="me-2" />
                          Delete
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default GetAboutUsPage;